import { useEffect, useState } from "react";
import { CheckCircle2, CircleAlert, Circle, Ban, Undo2, Sparkles, Check, X } from "lucide-react";
import { STATUS_STYLE } from "./wizardStatus";

const ICONS = { done: CheckCircle2, na: Ban, open: CircleAlert, optional: Circle, info: Circle };

// Ein Feld im Wizard mit Ampel-Status, Beschriftung und „Entfällt“-Schalter.
export default function WizardItem({ field, status, naInfo, onNa, compact, children, suggestion, aiApplied, onAiDecide }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.optional;
  const Icon = ICONS[status] || Circle;
  const label = compact ? (field.short_label || field.label) : field.label;
  const canNa = (status === "open" || status === "optional") && field.na_allowed !== false;

  return (
    <div
      data-item-key={field.key}
      data-testid={`wizard-item-${field.key}`}
      data-status={status}
      className={`relative rounded-xl border bg-[#101015] pl-4 pr-3 py-3 overflow-hidden ${st.border}`}
    >
      <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${st.bar}`} aria-hidden />
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex items-start gap-2">
          <Icon size={18} className={`mt-0.5 flex-shrink-0 ${st.text}`} />
          <div className="min-w-0">
            <div className="text-[14.5px] font-semibold text-zinc-100 leading-snug">
              {label}
              {field.unit && field.type !== "number" && <span className="text-zinc-500 font-normal"> ({field.unit})</span>}
              {aiApplied && (
                <span data-testid={`wizard-ai-badge-${field.key}`} title="Wert aus KI-Vorschlag übernommen"
                      className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-200 border border-amber-400/40">
                  <Sparkles size={9} /> KI
                </span>
              )}
            </div>
            <div className={`text-[11.5px] font-medium ${st.text}`}>
              {status === "done" && "Erledigt"}
              {status === "na" && "Entfällt"}
              {status === "open" && "Pflicht · noch offen"}
              {status === "optional" && "Optional"}
            </div>
          </div>
        </div>
        {canNa && onNa && (
          <button type="button" onClick={() => onNa(field.key, "")}
                  data-testid={`wizard-na-${field.key}`}
                  className="flex-shrink-0 h-9 px-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[12.5px] font-semibold active:scale-95 transition-transform">
            Entfällt
          </button>
        )}
        {status === "na" && onNa && (
          <button type="button" onClick={() => onNa(field.key, null)}
                  data-testid={`wizard-na-undo-${field.key}`}
                  className="flex-shrink-0 h-9 px-3 rounded-lg bg-zinc-800 text-zinc-200 text-[12.5px] font-semibold inline-flex items-center gap-1.5">
            <Undo2 size={13} /> Rückgängig
          </button>
        )}
      </div>
      {suggestion && onAiDecide && (
        <AiNote fieldKey={field.key} suggestion={suggestion} onDecide={(accept) => onAiDecide([field.key], accept)} />
      )}
      {status === "na"
        ? <NaReason fieldKey={field.key} reason={naInfo?.reason || ""} onSave={(r) => onNa(field.key, r)} />
        : children}
    </div>
  );
}

function NaReason({ fieldKey, reason, onSave }) {
  const [val, setVal] = useState(reason);
  useEffect(() => setVal(reason), [reason]);
  return (
    <input
      type="text"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => val !== reason && onSave(val)}
      placeholder="Grund (optional), z. B. kein Zirkulationskreis"
      data-testid={`wizard-na-reason-${fieldKey}`}
      className="w-full h-11 px-3 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-100 text-base focus:outline-none focus:border-emerald-500/60"
    />
  );
}

const fmtVal = (v) => (Array.isArray(v) ? v.join(", ") : v === true ? "Ja" : v === false ? "Nein" : String(v));

// Gelbe KI-Info-Bemerkung: Übernehmen oder Verwerfen — nichts wird automatisch eingetragen.
export function AiNote({ fieldKey, suggestion, onDecide, label }) {
  return (
    <div data-testid={`wizard-ai-note-${fieldKey}`}
         className="mb-2.5 rounded-lg border border-amber-400/50 bg-amber-400/[0.08] p-2.5">
      <div className="flex items-start gap-2">
        <Sparkles size={15} className="text-amber-300 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] text-amber-100">
            {label && <span className="text-amber-300/80">{label}: </span>}
            KI-Vorschlag: <b data-testid={`wizard-ai-value-${fieldKey}`}>{fmtVal(suggestion.value)}</b>
            <span className="text-[11.5px] text-amber-300/80"> · Sicherheit {suggestion.confidence}</span>
          </div>
          {suggestion.reason && <div className="text-[12px] text-zinc-400 mt-0.5">{suggestion.reason}</div>}
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => onDecide(true)} data-testid={`wizard-ai-accept-${fieldKey}`}
                className="flex-1 h-9 rounded-lg bg-emerald-500 text-zinc-950 text-[13px] font-bold inline-flex items-center justify-center gap-1">
          <Check size={14} /> Übernehmen
        </button>
        <button type="button" onClick={() => onDecide(false)} data-testid={`wizard-ai-reject-${fieldKey}`}
                className="flex-1 h-9 rounded-lg bg-zinc-800 text-zinc-200 text-[13px] font-semibold inline-flex items-center justify-center gap-1">
          <X size={14} /> Verwerfen
        </button>
      </div>
    </div>
  );
}
